"use client";

import { Observable } from "@noya-app/observable";
import { memoGeneric } from "@noya-app/react-utils";
import {
  MultiplayerUser,
  SharedConnectionDataManager,
  UserManager,
} from "@noya-app/state-manager";
import React, { useEffect, useMemo, useState } from "react";
import { useObservable } from "../useObservable";
type Point = { x: number; y: number };

export type UserPointerData = { pointer?: Point };

export type RenderUserPointerProps = {
  userId: string;
  name?: string;
  visible: boolean;
  point: Point;
};

export type RenderUserPointer = {
  renderUserPointer: (options: RenderUserPointerProps) => React.ReactNode;
};

type InternalUserPointerProps<E extends UserPointerData> = {
  user: MultiplayerUser;
  sharedConnectionDataManager: SharedConnectionDataManager<E>;
  hideAfter?: number;
} & RenderUserPointer;

function shouldShow(hideAfter: number, updatedAt: number) {
  return !!updatedAt && Date.now() - updatedAt < hideAfter;
}

const UserPointerInternal = memoGeneric(function UserPointerInternal<
  E extends UserPointerData,
>({
  user,
  sharedConnectionDataManager,
  hideAfter = 5000,
  renderUserPointer,
}: InternalUserPointerProps<E>) {
  const observable = useMemo(() => {
    const metadata$ = sharedConnectionDataManager.metadata$
      .observePath([user.connectionId] as const)
      .throttle(50);
    const data$ = sharedConnectionDataManager.data$
      .observePath([user.connectionId] as const)
      .throttle(50);

    return Observable.combine([metadata$, data$], ([metadata, data]) => {
      return { metadata, data };
    });
  }, [sharedConnectionDataManager, user.connectionId]);

  const { metadata, data } = useObservable(observable);

  const [, setForceUpdate] = useState(0);

  const updatedAt = metadata?.updatedAt ?? 0;
  const show = shouldShow(hideAfter, updatedAt);

  useEffect(() => {
    if (!show) return;

    const timeoutId = setTimeout(() => {
      setForceUpdate((prev) => prev + 1);
    }, hideAfter);

    return () => clearTimeout(timeoutId);
  }, [hideAfter, show, updatedAt]);

  if (!data?.pointer) return null;

  return renderUserPointer({
    userId: user.connectionId,
    name: user.name,
    point: data.pointer,
    visible: show,
  });
});

export type UserPointersOverlayProps<E extends UserPointerData> = {
  userManager: UserManager;
  sharedConnectionDataManager: SharedConnectionDataManager<E>;
} & RenderUserPointer;

export const UserPointersOverlay = memoGeneric(function UserPointers<
  E extends UserPointerData,
>({
  userManager,
  sharedConnectionDataManager,
  renderUserPointer,
}: UserPointersOverlayProps<E>) {
  const currentConnectionId = useObservable(
    sharedConnectionDataManager.currentConnectionId$
  );
  const connectedUsers = useObservable(userManager.connectedUsers$);

  return (
    <>
      {connectedUsers.map((user) => {
        if (user.connectionId === currentConnectionId) return null;

        return (
          <UserPointerInternal
            key={user.connectionId}
            user={user}
            sharedConnectionDataManager={
              sharedConnectionDataManager as unknown as SharedConnectionDataManager<UserPointerData>
            }
            renderUserPointer={renderUserPointer}
          />
        );
      })}
    </>
  );
});
